/**
 * WyCo Analytics Tracker
 * Lightweight, privacy-friendly analytics for the landing page.
 * No cookies - uses sessionStorage for session tracking.
 */

const ANALYTICS_ENDPOINT = process.env.REACT_APP_BACKEND_URL + '/api/analytics/site/track';

// Generate session ID (stored in sessionStorage, cleared on tab close)
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('wyco_session');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    sessionStorage.setItem('wyco_session', sessionId);
  }
  return sessionId;
};

// Track scroll depth
let maxScrollDepth = 0;
const updateScrollDepth = () => {
  const scrollTop = window.scrollY;
  const docHeight = document.documentElement.scrollHeight - window.innerHeight;
  const scrollPercent = docHeight > 0 ? Math.round((scrollTop / docHeight) * 100) : 0;
  maxScrollDepth = Math.max(maxScrollDepth, scrollPercent);
};

// Track time on page
let pageLoadTime = Date.now();

// Send tracking event
const sendEvent = async (eventData) => {
  try {
    await fetch(ANALYTICS_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...eventData,
        session_id: getSessionId(),
        page_url: window.location.href,
        page_path: window.location.pathname,
        referrer: document.referrer,
        screen_width: window.screen.width,
        screen_height: window.screen.height,
      }),
      // Use keepalive for beforeunload events
      keepalive: true,
    });
  } catch (e) {
    // Silently fail - don't break the site for analytics
    console.debug('Analytics event failed:', e);
  }
};

// Initialize analytics
export const initAnalytics = () => {
  // Track pageview on load
  sendEvent({ type: 'pageview' });
  
  // Track scroll depth
  window.addEventListener('scroll', updateScrollDepth, { passive: true });
  
  // Track exit (time on page + scroll depth)
  const trackExit = () => {
    const timeOnPage = Math.round((Date.now() - pageLoadTime) / 1000); // seconds
    sendEvent({
      type: 'exit',
      time_on_page: timeOnPage,
      scroll_depth: maxScrollDepth,
      is_exit: true,
    });
  };
  
  // Send exit data when user leaves
  window.addEventListener('beforeunload', trackExit);
  
  // Also track visibility change (mobile tab switching)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      trackExit();
    }
  });
};

// Track specific events (for future use)
export const trackEvent = (eventName, eventData = {}) => {
  sendEvent({
    type: 'event',
    event_name: eventName,
    ...eventData,
  });
};

// Track page navigation (for SPA)
export const trackPageView = (path) => {
  pageLoadTime = Date.now();
  maxScrollDepth = 0;
  sendEvent({
    type: 'pageview',
    page_path: path,
  });
};

export default { initAnalytics, trackEvent, trackPageView };
